import { useCallback, useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { DashboardChart } from '@/components/DashboardChart'
import { fetchDistributors, fetchDistributorPayments } from '@/lib/distributors'
import type {
  Distributor,
  DistributorPayment,
  DistributorPaymentPagination,
} from '@/types/distributors'
import styles from './DistribuidoresRelatorioPage.module.css'

const PIE_COLORS = ['#5e17eb', '#22c55e', '#f59e0b', '#ef4444', '#0ea5e9', '#d946ef']

function formatCurrency(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function DistribuidoresRelatorioPage() {
  const [distributors, setDistributors] = useState<Distributor[]>([])
  const [payments, setPayments] = useState<DistributorPayment[]>([])
  const [pagination, setPagination] = useState<DistributorPaymentPagination | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filterDistributorId, setFilterDistributorId] = useState<number | ''>('')
  const [currentPage, setCurrentPage] = useState(1)

  const loadDistributors = useCallback(async () => {
    const res = await fetchDistributors()
    setDistributors(res.distributors)
  }, [])

  const loadPayments = useCallback(async (page: number, distributorId: number | '') => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchDistributorPayments({
        page,
        per_page: 20,
        ...(distributorId !== '' ? { distributor_id: distributorId } : {}),
      })
      setPayments(res.items)
      setPagination(res.pagination)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadDistributors()
  }, [loadDistributors])

  useEffect(() => {
    void loadPayments(currentPage, filterDistributorId)
  }, [loadPayments, currentPage, filterDistributorId])

  const handleFilterChange = (distributorId: number | '') => {
    setFilterDistributorId(distributorId)
    setCurrentPage(1)
  }

  const pieData = useMemo(() => {
    const map = new Map<string, number>()
    for (const p of payments) {
      const key = p.distributor.name
      map.set(key, (map.get(key) ?? 0) + p.distributor_commission_amount)
    }
    return Array.from(map.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }))
  }, [payments])

  const totalPages = pagination?.last_page ?? 1

  return (
    <div className={styles.page}>
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Distribuidor</label>
          <select
            className={styles.select}
            value={filterDistributorId}
            onChange={(e) =>
              handleFilterChange(e.target.value === '' ? '' : Number(e.target.value))
            }
          >
            <option value="">Todos os distribuidores</option>
            {distributors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.distributor_name}
              </option>
            ))}
          </select>
        </div>

        <button
          className={styles.btnExport}
          onClick={() => window.print()}
          title="Exportar dados"
        >
          <Icon icon="mdi:download-outline" />
          Exportar Dados
        </button>
      </div>

      {payments.length > 0 && (
        <DashboardChart title="Distribuição de Comissões por Distribuidor">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) =>
                  `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                }
                labelLine={false}
              >
                {pieData.map((_, index) => (
                  <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => formatCurrency(typeof value === 'number' ? value : 0)}
                contentStyle={{
                  background: '#ffffff',
                  border: '1px solid rgba(59,22,54,0.2)',
                  borderRadius: 8,
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </DashboardChart>
      )}

      {isLoading ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
          Carregando...
        </div>
      ) : error ? (
        <div className={styles.centered}>
          <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
          {error}
          <button
            className={styles.retryBtn}
            onClick={() => void loadPayments(currentPage, filterDistributorId)}
          >
            Tentar novamente
          </button>
        </div>
      ) : payments.length === 0 ? (
        <div className={styles.centered}>
          <Icon icon="mdi:file-chart-outline" className={styles.emptyIcon} />
          Nenhum pagamento encontrado para os filtros selecionados.
        </div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Data</th>
                  <th>Campanha</th>
                  <th>Cliente</th>
                  <th>Distribuidor</th>
                  <th>Afiliado</th>
                  <th>Status</th>
                  <th>% Dist.</th>
                  <th>Comissão distribuidor</th>
                  <th>Comissão afiliado</th>
                  <th>Qtd. Números</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.payment_id}>
                    <td className={styles.idCell}>#{p.payment_id}</td>
                    <td className={styles.dateCell}>{formatDate(p.paid_at)}</td>
                    <td>
                      <span className={styles.campaignLink} title={p.campaign.title}>
                        {p.campaign.title || p.campaign.name}
                      </span>
                    </td>
                    <td>
                      <div className={styles.nameCell}>
                        <span>{p.buyer.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className={styles.affiliateName}>{p.distributor.name}</span>
                    </td>
                    <td>
                      {p.affiliate ? (
                        <span className={styles.affiliateName}>{p.affiliate.name}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span className={styles.statusBadge}>{p.status}</span>
                    </td>
                    <td>{p.distributor.commission_percent}%</td>
                    <td className={styles.amountCell}>
                      {formatCurrency(p.distributor_commission_amount)}
                    </td>
                    <td className={styles.amountCell}>
                      {p.affiliate
                        ? formatCurrency(p.affiliate_commission_amount)
                        : '—'}
                    </td>
                    <td>{p.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
            >
              Primeiro
            </button>
            <button
              className={styles.pageBtn}
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <Icon icon="mdi:chevron-left" /> Anterior
            </button>

            <span className={styles.pageInfo}>
              Página {currentPage} de {totalPages}
              {pagination && ` · ${pagination.total} registros`}
            </span>

            <button
              className={styles.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próximo <Icon icon="mdi:chevron-right" />
            </button>
            <button
              className={styles.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              Último
            </button>
          </div>
        </>
      )}
    </div>
  )
}
